import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, UtensilsCrossed, Users, Phone, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { buildTableConfirmUrl } from "@/lib/quote";
import { useDynamicManifest } from "@/lib/useDynamicManifest";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const DAYS_FULL = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
const pad = (n) => String(n).padStart(2, "0");

const b64ToUint8 = (b64) => {
  const pad2 = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad2).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

export default function TablesCalendar() {
  const { token } = useParams();
  const cleanToken = (token || "").trim().replace(/[^a-fA-F0-9-]/g, "");
  const [reservations, setReservations] = useState(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [month, setMonth] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [lastSeen, setLastSeen] = useState(() => localStorage.getItem("bliss_tavoli_last_seen") || "");
  const [pushState, setPushState] = useState("idle");
  const pushSupported = "serviceWorker" in navigator && "PushManager" in window && typeof Notification !== "undefined";
  const isStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
  useDynamicManifest("Calendario Bliss", "Calendario Bliss", "tavoli");

  useEffect(() => {
    if (!pushSupported) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((s) => { if (s && Notification.permission === "granted") setPushState("enabled"); })
      .catch(() => {});
  }, [pushSupported]);

  const enablePush = async () => {
    try {
      setPushState("loading");
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setPushState("denied"); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-key");
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.key) });
      await api.post(`/push/subscribe/${cleanToken}`, { subscription: sub.toJSON() });
      setPushState("enabled");
    } catch {
      setPushState("error");
    }
  };

  const testPush = () => api.post(`/push/test/${cleanToken}`).catch(() => {});

  useEffect(() => {
    let alive = true;
    const load = () => api.get(`/staff-calendar/${cleanToken}`)
      .then((r) => {
        if (!alive) return;
        setError("");
        const res = r.data.reservations || [];
        setReservations(res);
        const stored = localStorage.getItem("bliss_tavoli_last_seen") || "";
        const unseen = res.filter((x) => stored && x.created_at && x.created_at > stored).length;
        if (document.visibilityState === "visible") {
          localStorage.setItem("bliss_tavoli_last_seen", new Date().toISOString());
          navigator.clearAppBadge?.().catch(() => {});
        } else if (unseen > 0) {
          navigator.setAppBadge?.(unseen).catch(() => {});
        }
      })
      .catch((e) => {
        if (!alive) return;
        if (e?.response?.status === 401) setError("Link non valido o scaduto. Copia il link aggiornato dal gestionale (tab Date).");
        else if (!document.hidden) setError("retry");
      });
    load();
    const interval = setInterval(load, 60000);
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        setLastSeen(localStorage.getItem("bliss_tavoli_last_seen") || "");
        load();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => { alive = false; clearInterval(interval); document.removeEventListener("visibilitychange", onVisible); };
  }, [cleanToken, attempt]);

  const isNew = (r) => Boolean(lastSeen && r.created_at && r.created_at > lastSeen);

  if (error === "retry")
    return (
      <div className="min-h-screen bg-[#FBF6F0] flex flex-col items-center justify-center p-6 gap-4">
        <p data-testid="tables-cal-network-error" className="text-sm text-[#8a7a6d] text-center">Connessione non riuscita. Controlla la rete e riprova.</p>
        <button data-testid="tables-cal-retry" onClick={() => setAttempt((a) => a + 1)} className="bg-[#C85A32] text-white text-sm font-bold px-6 py-2.5 rounded-full">Riprova</button>
      </div>
    );
  if (error)
    return <div className="min-h-screen bg-[#FBF6F0] flex items-center justify-center p-6"><p data-testid="tables-cal-error" className="text-sm text-[#8a7a6d] text-center">{error}</p></div>;
  if (!reservations)
    return <div className="min-h-screen bg-[#FBF6F0] flex items-center justify-center text-sm text-[#8a7a6d]">Caricamento…</div>;

  const prefix = `${month.y}-${pad(month.m + 1)}`;
  const monthRes = reservations.filter((r) => r.date.startsWith(prefix));
  const byDate = {};
  monthRes.forEach((r) => { (byDate[r.date] = byDate[r.date] || []).push(r); });
  const t = new Date();
  const todayStr = `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
  const nav = (dir) => setMonth(({ y, m }) => { const d = new Date(y, m + dir, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const totCoperti = monthRes.reduce((s, r) => s + r.people, 0);
  const newAll = reservations.filter(isNew);

  return (
    <div className="min-h-screen bg-[#FBF6F0] text-[#2C1A11]">
      <header className="bg-[#2C1A11] text-white px-4 py-3 sticky top-0 z-20">
        <div className="max-w-2xl mx-auto flex items-center gap-2.5">
          <img src="/logo.png" alt="Bliss Café" className="h-8 w-auto" />
          <span className="font-display font-bold flex items-center gap-2"><UtensilsCrossed className="w-4 h-4 text-[#F0B429]" /> Prenotazioni tavoli</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5">
        {newAll.length > 0 && (
          <div data-testid="tables-new-banner" className="bg-[#C85A32] text-white rounded-2xl px-4 py-3 mb-4 text-sm font-semibold flex items-center gap-2">
            <span className="bg-white text-[#C85A32] font-bold rounded-full w-6 h-6 flex items-center justify-center text-xs shrink-0">{newAll.length}</span>
            {newAll.length === 1 ? "Nuova prenotazione" : "Nuove prenotazioni"} dall'ultima apertura: {newAll.map((r) => `${r.name} (${r.date.slice(8)}/${r.date.slice(5, 7)})`).join(", ")}
          </div>
        )}
        <div className="flex items-center justify-between mb-1">
          <button data-testid="tables-cal-prev" onClick={() => nav(-1)} className="p-2 text-[#8a7a6d] hover:text-[#C85A32]"><ChevronLeft className="w-5 h-5" /></button>
          <h1 data-testid="tables-cal-month" className="font-display text-xl font-bold">{MONTHS[month.m]} {month.y}</h1>
          <button data-testid="tables-cal-next" onClick={() => nav(1)} className="p-2 text-[#8a7a6d] hover:text-[#C85A32]"><ChevronRight className="w-5 h-5" /></button>
        </div>
        {monthRes.length > 0 && (
          <p data-testid="tables-cal-summary" className="text-center text-xs text-[#8a7a6d] mb-4">{monthRes.length} prenotazion{monthRes.length === 1 ? "e" : "i"} · {totCoperti} coperti nel mese</p>
        )}

        {monthRes.length === 0 && <p data-testid="tables-cal-empty" className="text-sm text-[#8a7a6d] text-center py-12">Nessuna prenotazione questo mese</p>}

        <div className="space-y-3">
          {Object.entries(byDate).map(([date, rs]) => {
            const d = new Date(`${date}T12:00:00`);
            return (
              <div key={date} data-testid={`tables-day-${date}`}
                className={`bg-white rounded-2xl border p-4 ${date === todayStr ? "border-[#C85A32] ring-1 ring-[#C85A32]/40" : "border-[#e8ddd0]"}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <div className="font-display font-bold">{DAYS_FULL[d.getDay()]} {d.getDate()}</div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#8a7a6d] flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {rs.reduce((s, r) => s + r.people, 0)} coperti · {rs.length} tavol{rs.length === 1 ? "o" : "i"}</span>
                    {date === todayStr && <span className="text-[11px] font-bold bg-[#C85A32] text-white px-2.5 py-0.5 rounded-full">OGGI</span>}
                  </div>
                </div>
                <div className="space-y-1.5">
                  {rs.map((r) => (
                    <div key={r.id} data-testid={`tables-res-${r.id}`} className={`flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm flex-wrap ${isNew(r) ? "bg-[#C85A32]/10 ring-1 ring-[#C85A32]/50" : "bg-[#FBF6F0]"}`}>
                      {isNew(r) && <span data-testid={`tables-new-${r.id}`} className="text-[10px] font-bold bg-[#C85A32] text-white px-2 py-0.5 rounded-full">NUOVA</span>}
                      <b className="font-mono-num text-[#C85A32]">{r.time || "—"}</b>
                      <span className="font-semibold">{r.name}</span>
                      <span className="text-xs text-[#8a7a6d]">x{r.people}{r.children > 0 ? ` (${r.children} bimbi)` : ""}</span>
                      {r.time_slot && <span className="text-[10px] uppercase font-bold text-[#4A6B5D]">{r.time_slot}</span>}
                      <span className="ml-auto flex items-center gap-2">
                        {r.phone && (
                          <a href={`tel:${r.phone}`} data-testid={`tables-call-${r.id}`} className="text-xs text-[#C85A32] flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone}</a>
                        )}
                        <a href={buildTableConfirmUrl(r)} target="_blank" rel="noreferrer" data-testid={`tables-confirm-${r.id}`} title="Conferma il tavolo su WhatsApp"
                          className="flex items-center gap-1 bg-[#25D366] text-white text-[11px] font-bold px-2.5 py-1 rounded-full">
                          <MessageCircle className="w-3 h-3" /> Conferma
                        </a>
                      </span>
                      {r.notes && <span className="w-full text-xs text-[#8a7a6d] italic">{r.notes}</span>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-8 space-y-3">
          {pushSupported && (pushState === "idle" || pushState === "loading" || pushState === "error") && (
            <button data-testid="enable-push-button" onClick={enablePush} disabled={pushState === "loading"}
              className="bg-[#2C1A11] text-white text-xs font-bold px-5 py-2.5 rounded-full disabled:opacity-60">
              {pushState === "loading" ? "Attivazione…" : "🔔 Attiva le notifiche prenotazioni"}
            </button>
          )}
          {pushState === "error" && <p className="text-[11px] text-[#C85A32]">Attivazione non riuscita. Riprova.</p>}
          {pushState === "denied" && (
            <p data-testid="push-denied-note" className="text-[11px] text-[#C85A32]">Permesso negato: attivalo da Impostazioni → Notifiche → Calendario Bliss</p>
          )}
          {pushState === "enabled" && (
            <div className="space-y-2">
              <p data-testid="push-active-note" className="text-[11px] text-[#4A6B5D] font-semibold">✓ Notifiche attive: riceverai un avviso a ogni nuova prenotazione</p>
              <button data-testid="test-push-button" onClick={testPush} className="text-[11px] text-[#8a7a6d] underline">Invia notifica di prova</button>
            </div>
          )}
          {!pushSupported && !isStandalone && (
            <p data-testid="push-install-hint" className="text-[11px] text-[#8a7a6d]">
              🔔 Per attivare le notifiche: salva prima questa pagina sulla schermata Home (Condividi → Aggiungi alla schermata Home) e aprila dall'icona.
            </p>
          )}
          <p className="text-[11px] text-[#8a7a6d]">
            Aggiungi questa pagina alla schermata Home per aprirla con un tocco.
          </p>
        </div>
      </main>
    </div>
  );
}
